/**
 * The one person who runs StandingDocs for the customer.
 *
 * Their System Admin adds everyone else, decides what each person may do, and
 * answers for who has access. Only we can name them, and they must use an
 * authenticator app — an organization administrator cannot grant this.
 *
 * The page this replaces listed a pending System Admin invitation in TWO
 * places: here, with no way to resend it, and again in a separate panel at the
 * very bottom that had the button. One list now, with the button on the row.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../../lib/callable';
import { ResetMfaButton } from '../../ResetMfaButton';
import { EMAIL_MAX } from '../../../../types/fieldLimits';
import page from '../../../../shared/Page.module.css';
import own from '../Onboarding.module.css';
import fields from '../../../../shared/Form.module.css';
import { StageNeeds } from '../StageNeeds';
import type { Onboarding, Stage } from '../types';
import { GroupCard } from '../../../../shared/GroupCard';

export function AdministratorsStage({ data, stage }: { data: Onboarding; stage: Stage }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['onboarding', data.orgId] });

  const name = useMutation({
    mutationFn: (to: string) =>
      call<{ orgId: string; email: string; grant: boolean }, { how: string; emailSent?: boolean }>(
        'sysAdminSetOrgSysAdmin', { orgId: data.orgId, email: to, grant: true },
      ),
    onSuccess: (r, to) => {
      setEmail('');
      setNotice(r.emailSent === false
        ? `${to} is named, but the email could not be sent. Resend it once email works.`
        : `${to} is invited. They sign in with the emailed link and set up an authenticator app before anything opens.`);
      refresh();
    },
  });

  const resend = useMutation({
    // `inviteId`, not an email. The callable reads request.data.inviteId and
    // refuses without one; sending an address here would have failed every time.
    mutationFn: (invite: { id: string; email: string }) =>
      call('sysAdminResendInvite', { orgId: data.orgId, inviteId: invite.id }),
    onSuccess: (_r, invite) => { setNotice(`Invitation resent to ${invite.email}.`); refresh(); },
  });

  const busy = name.isPending || resend.isPending;
  const error = name.error ?? resend.error;

  return (
    <>
      {error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(error) ? error.message : 'That did not go through.'}
        </div>
      )}
      {notice && <div className={`${page.notice} ${page.noticeInfo}`} role="status">{notice}</div>}

      <GroupCard title="System Admins" list>
      {data.sysAdmins.length === 0 && data.pendingSysAdminInvites.length === 0 && (
        <p className={page.absent} style={{ padding: 'var(--sp-4) 0' }}>
          Nobody yet. Until somebody is named, only we can set this organization up — they cannot
          add a single member of their own staff.
        </p>
      )}

      <ul className={own.reqList} aria-label="System Admins">
        {data.sysAdmins.map((person) => (
          <li key={person.email} className={own.personRow}>
            <div className={own.personBody}>
              <div className={page.itemName}>{person.displayName || person.email}</div>
              <div className={page.itemMeta}>
                {person.displayName ? `${person.email} · ` : ''}
                {person.enrolled
                  ? 'Authenticator app set up'
                  : 'No authenticator app yet — the role waits until there is one'}
              </div>
            </div>
            <div className={page.actions}>
              {person.enrolled && (
                <ResetMfaButton
                  orgId={data.orgId}
                  targetUid={person.uid}
                  who={person.email}
                  onDone={setNotice}
                  invalidate={[['onboarding', data.orgId]]}
                />
              )}
            </div>
          </li>
        ))}

        {data.pendingSysAdminInvites.map((invite, i) => (
          <li key={invite.email} className={own.personRow}>
            <div className={own.personBody}>
              <div className={page.itemName}>{invite.email}</div>
              <div className={page.itemMeta}>Invited, not signed in yet.</div>
            </div>
            <div className={page.actions}>
              <button type="button" className={page.btn} disabled={busy}
                id={i === 0 ? 'sysadmin-resend' : undefined}
                aria-label={`Resend the invitation to ${invite.email}`}
                onClick={() => resend.mutate(invite)}>
                {resend.isPending ? 'Sending…' : 'Resend invitation'}
              </button>
            </div>
          </li>
        ))}
      </ul>
      </GroupCard>

      <GroupCard title="Name a System Admin">
      <form
        style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'end' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim() && email.includes('@')) name.mutate(email.trim());
        }}
      >
        <label htmlFor="sysadmin-email" className={fields.label} style={{ marginBottom: 0 }}>
          Their email address
          <input
            id="sysadmin-email"
            type="email"
            className={fields.input}
            style={{ display: 'block', minWidth: 260, marginTop: 'var(--sp-2)' }}
            autoComplete="off"
            maxLength={EMAIL_MAX}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <button type="submit" className={`${page.btn} ${page.btnPrimary}`} disabled={busy || !email.trim()}>
          {name.isPending ? 'Saving…' : 'Name them System Admin'}
        </button>
      </form>
      </GroupCard>

      <StageNeeds requirements={stage.requirements} />
    </>
  );
}
